import moment from 'moment';
import { actions } from '../../context';
import { Field, Form } from 'react-final-form';
import { useDispatch } from 'react-redux';
import { Button, ModalFooter, Row, Col, FormGroup, Label, Input } from 'reactstrap';
import { useEffect, useState } from 'react';

const durations = [`Hour`, `Day`, `Week`, `Month`, `Year`];
const emptyGuid = '00000000-0000-0000-0000-000000000000';

function MutePracticeForm({ practiceId, currentPracticeId }) {
    const dispatch = useDispatch();
    const [muteReasons, setMuteReasons] = useState([]);

    const submit = async (vals) => {
        const payload = {
            ...vals,
            muteUntil: moment(vals.muteUntil).unix(),
        };

        dispatch(actions.practice.mutePractice(practiceId, payload))
            .then(() => {
                dispatch(actions.toggle.triggerRefresh());
                if (practiceId === currentPracticeId)
                    dispatch(actions.toggle.triggerPracticeRefresh());
            })
            .then(() =>
                dispatch(actions.toggle.toggleMutePracticeModal(false))
            );
    };

    useEffect(() => {
        dispatch(actions.muteReasons.fetchMuteReasons()).then((res) => {
            setMuteReasons(res || []);
        });
    }, [dispatch]);

    const validate = (values) => {
        const errors = {};
        if (!values.muteReasonId || values.muteReasonId === emptyGuid) {
            errors.muteReasonId = 'Required';
        }
        if (values.interval <= 0) {
            errors.interval = 'Must be at least 1';
        }
        if (values.muteUntil && moment(values.muteUntil).isSameOrBefore(moment())) {
            errors.muteUntil = 'Must be a future date and time';
        }
        return errors;
    };

    return (
        <Form
            onSubmit={submit}
            initialValues={{
                muteUntil: moment().toDate(),
                interval: 0,
                frequency: 0,
                muteReasonId: emptyGuid,
            }}
            validate={validate}
            render={({ handleSubmit, form, submitting, pristine, values }) => (
                <form onSubmit={handleSubmit}>
                    <Row>
                        <Col className="px-2 my-2">
                            <div className="d-flex justify-content-center align-items-center opaque p-3">
                                <div className="text-dark">Mute for</div>
                                &emsp;
                                <div className="w-25">
                                    <Field name="interval">
                                        {({ input, meta }) => (
                                            <Input
                                                {...input}
                                                type="number"
                                                min={1}
                                                invalid={meta.error && (meta.touched || meta.dirty)}
                                                style={{ textAlign: 'center' }}
                                                onChange={(e) => {
                                                    input.onChange(e);
                                                    const newMuteUntil = moment()
                                                        .add(
                                                            e.target.value,
                                                            durations[values.frequency].toLowerCase()
                                                        )
                                                        .toDate();
                                                    form.change('muteUntil', newMuteUntil);
                                                }}
                                            />
                                        )}
                                    </Field>
                                </div>
                                &emsp;
                                <div className="w-25">
                                    <Field name="frequency">
                                        {({ input }) => (
                                            <Input
                                                {...input}
                                                type="select"
                                                id="muteFrequencySelector"
                                                onChange={(e) => {
                                                    input.onChange(e);
                                                    const muteUntilDate = moment()
                                                        .add(
                                                            values.interval,
                                                            `${durations[e.target.value].toLowerCase()}s`
                                                        )
                                                        .toDate();
                                                    form.change('muteUntil', muteUntilDate);
                                                }}
                                            >
                                                {durations.map((d, x) => (
                                                    <option key={x} value={x}>
                                                        {d}{values.interval !== 1 ? 's' : ''}
                                                    </option>
                                                ))}
                                            </Input>
                                        )}
                                    </Field>
                                </div>
                            </div>
                            <Field name="interval">
                                {({ meta }) =>
                                    meta.error && (meta.touched || meta.dirty) ? (
                                        <div className="text-danger small mt-1 text-center">{meta.error}</div>
                                    ) : null
                                }
                            </Field>
                        </Col>
                    </Row>
                    <Row className="mt-2">
                        <Col className="px-2 my-2">
                            <FormGroup>
                                <Label for="muteUntilDate">Mute Until Date</Label>
                                <Field name="muteUntil">
                                    {({ input, meta }) => (
                                        <>
                                            <Input
                                                type="date"
                                                id="muteUntilDate"
                                                value={moment(input.value).format('YYYY-MM-DD')}
                                                min={moment().format('YYYY-MM-DD')}
                                                invalid={meta.error && (meta.touched || meta.dirty)}
                                                onChange={(e) => {
                                                    const currentTime = moment(input.value);
                                                    const newDate = moment(e.target.value)
                                                        .hour(currentTime.hour())
                                                        .minute(currentTime.minute())
                                                        .toDate();
                                                    input.onChange(newDate);
                                                }}
                                            />
                                            {meta.error && (meta.touched || meta.dirty) && (
                                                <div className="text-danger small mt-1">{meta.error}</div>
                                            )}
                                        </>
                                    )}
                                </Field>
                            </FormGroup>
                        </Col>
                        <Col className="px-2 my-2">
                            <FormGroup>
                                <Label for="muteUntilTime">Mute Until Time</Label>
                                <Field name="muteUntil">
                                    {({ input, meta }) => (
                                        <>
                                            <Input
                                                type="time"
                                                id="muteUntilTime"
                                                value={moment(input.value).format('HH:mm')}
                                                invalid={meta.error && (meta.touched || meta.dirty)}
                                                onChange={(e) => {
                                                    const [hours, minutes] = e.target.value.split(':');
                                                    const newDateTime = moment(input.value)
                                                        .hour(parseInt(hours))
                                                        .minute(parseInt(minutes))
                                                        .toDate();
                                                    input.onChange(newDateTime);
                                                }}
                                            />
                                            {meta.error && (meta.touched || meta.dirty) && (
                                                <div className="text-danger small mt-1">{meta.error}</div>
                                            )}
                                        </>
                                    )}
                                </Field>
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row className="mt-2">
                        <Col className="px-2 my-2">
                            <FormGroup>
                                <Label for="muteReasonSelector">Reason</Label>
                                <Field name="muteReasonId">
                                    {({ input, meta }) => (
                                        <div>
                                            <Input
                                                {...input}
                                                type="select"
                                                id="muteReasonSelector"
                                                invalid={meta.error && meta.touched}
                                            >
                                                <option value={emptyGuid} disabled>
                                                    --Select a reason category--
                                                </option>
                                                {muteReasons
                                                    .sort(compareReasonName)
                                                    .map((reason) => (
                                                        <option key={reason.id} value={reason.id}>
                                                            {reason.reason}
                                                        </option>
                                                    ))}
                                            </Input>
                                            {meta.error && meta.touched && (
                                                <div className="text-danger small mt-1">{meta.error}</div>
                                            )}
                                        </div>
                                    )}
                                </Field>
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row className="mt-2">
                        <Col className="px-2 my-2">
                            <FormGroup>
                                <Label for="muteNote">Notes</Label>
                                <Field name="muteNote">
                                    {({ input }) => (
                                        <Input
                                            {...input}
                                            type="textarea"
                                            id="muteNote"
                                            placeholder="Reason for muting"
                                            rows="4"
                                        />
                                    )}
                                </Field>
                            </FormGroup>
                        </Col>
                    </Row>
                    <ModalFooter>
                        <Button
                            color="danger"
                            onClick={() =>
                                pristine
                                    ? dispatch(
                                          actions.toggle.toggleMutePracticeModal(
                                              false
                                          )
                                      )
                                    : form.restart()
                            }>
                            {pristine ? 'Cancel' : 'Reset'}
                        </Button>
                        <Button
                            color="info"
                            disabled={
                                pristine ||
                                submitting ||
                                form.getState().hasValidationErrors
                            }
                            type="submit">
                            Mute
                        </Button>
                    </ModalFooter>
                </form>
            )}></Form>
    );
}

function compareReasonName(a, b) {
    if (a.reason < b.reason) {
        return -1;
    }
    if (a.reason > b.reason) {
        return 1;
    }
    return 0;
}

export default MutePracticeForm;
